// client/src/pages/usecases/StartupValidationPage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../index.css';
import '../../styles/HomePage.css';
import './usecases.css';
import { event } from '../../services/analyticsService';
import { useAuth } from '../../authentication/AuthContext';

function StartupValidationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view with use case type
    event({
      action: 'view_use_case_detail',
      category: 'engagement',
      label: 'startup_validation'
    });
  }, []);

  const handleTryTemplate = () => {
    // Track template usage
    event({
      action: 'try_template',
      category: 'conversion',
      label: 'startup_validation_template'
    });
    
    // Check if user is authenticated
    if (!user) {
      navigate('/login', { 
        state: { 
          from: '/research',
          message: 'Please log in to access the app and try this template'
        } 
      });
      return;
    }
    
    navigate('/research', { 
      state: { template: 'product-validation' } 
    });
  };

  const handleViewExample = (exampleType) => {
    // Track example view
    event({
      action: 'view_example',
      category: 'engagement',
      label: `startup_validation_${exampleType}`
    });
    
    navigate('/exampleresearches', { 
      state: { 
        filter: 'startup-validation', 
        example: exampleType 
      } 
    });
  };

  const benefits = [
    {
      title: "Save Weeks of Research Time",
      description: "Get feedback on your concept in minutes instead of waiting weeks for traditional research",
      alt: "Time savings"
    },
    {
      title: "Reduce Costs",
      description: "Validate ideas before expensive development, saving thousands in potential misdirected resources",
      alt: "Cost reduction"
    },
    {
      title: "Iterate Rapidly",
      description: "Test multiple approaches or variations in hours, not months",
      alt: "Rapid iterations"
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Describe Your Concept",
      description: "Enter your product idea, value proposition, or problem statement. Our AI creates a targeted survey to test market viability."
    },
    {
      number: 2,
      title: "Select Your Target Audience",
      description: "Choose demographic attributes that match your ideal customers - age, income, education, and more."
    },
    {
      number: 3,
      title: "Get Simulated Responses",
      description: "Our AI personas respond to your survey as real people would, providing authentic feedback on your concept."
    },
    {
      number: 4,
      title: "Analyze Insights",
      description: "Review response patterns and demographic breakdowns to understand potential market reception."
    }
  ];

  // Define case study findings data
  const findings = [
    "<strong>85%</strong> of target users found the core value proposition <span class='highlight'>compelling</span>",
    "Users <strong>25-34</strong> showed highest purchase intent",
    "Subscription pricing was <span class='highlight'>preferred</span> over one-time payment",
    "Calendar integration was the most requested feature"
  ];

  return (
    <div className="pricing-page">
      <div className="top-hero">
        <div className="hero-grid-item">
          <h4 style={{fontWeight: '400'}}>USE CASE</h4>
          <h1 style={{fontSize: '4rem', marginBottom: "3vh", marginTop: "3vh"}}>Startup & Product Validation</h1>
          <h3 style={{fontWeight: '400'}}>Validate Your Concept Before You Build</h3>

          
          <div className="button-container">
            <button onClick={handleTryTemplate} className="standout-button button">Try This Template</button>
          </div>
        </div>
      </div>

      <div className="hero" style={{backgroundColor: 'var(--secondary-color', padding: 0, marginTop: '5vh'}}> 
        <div className="content-grid-2" style={{margin: '6vh 10vw', gap: '7rem', justifyContent: 'center'}}>
          <div>
            <h2 style={{fontSize: '2.5rem', fontWeight: '500', position: 'relative', paddingLeft: '2rem'}}>
              <span style={{
                position: 'absolute',
                left: '-1rem',
                top: '-2rem',
                fontSize: '6rem',
                fontFamily: 'serif',
                color: 'var(--tertiary-color)',
                opacity: '0.7'
              }}>"</span>
              
              <i>Successful products aren't built on guesswork. They're built on validation.</i>
              <span style={{
                position: 'absolute',
                right: '-1.5rem',
                bottom: '-2rem',
                fontSize: '6rem',
                fontFamily: 'serif',
                color: 'var(--tertiary-color)',
                opacity: '0.7'
              }}>"</span>
            </h2>
          </div>
          <div style={{textAlign: 'center'}}>
            <p style={{lineHeight: '1.7', letterSpacing: '0.05em', fontWeight: '500', padding: "3vh 0", boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', borderRadius: '10px', padding: '3vh 2vw', backgroundColor: 'var(--background-color)'}}>
              InstantFlows helps startups and product teams <span style={{color: 'var(--tertiary-color)'}}>quickly assess</span> the viability and appeal of new concepts.
              <br/> <br/>
              Avoid expensive prototyping or lengthy market research cycles with instant feedback.
            </p>
          </div>
        </div>
      </div>

      <div className="benefits-section">
        <h2 className="benefits-title">Key Benefits</h2>
        <div className="benefits-container">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-header">
                <div className="icon-container">
                  <img src="/InstantFlowsicon_wb.png" alt={benefit.alt} className="benefit-icon" />
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
              </div>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="hero hero-3">
        <h2>How It Works</h2>
        <div className="content-grid-4" style={{maxWidth: "1200px", margin: "0 4rem", gap: "1.5rem"}}>
          {steps.map((step, index) => (
            <div key={index} className="card" style={{padding: "1.5rem", transition: "transform 0.3s ease", marginBottom: '1rem'}}>
              <div style={{display: "flex", alignItems: "center", marginBottom: "0.5rem"}}>
                <div style={{
                  background: "var(--tertiary-color)", 
                  color: "white", 
                  width: "32px", 
                  height: "32px", 
                  borderRadius: "50%", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  fontWeight: "bold",
                  marginRight: "0.8rem"
                }}>{step.number}</div>
                <h3 style={{margin: "0", fontWeight: '500'}}>{step.title}</h3>
              </div>
              <p style={{margin: "0", textAlign: 'left'}}>{step.description}</p>
            </div>
          ))}
        </div>
        <div className="button-container" style={{maxHeight: "none", paddingBottom: "1rem"}}>
          <button onClick={handleTryTemplate} className="standout-button button">Try this Template</button>
        </div>
      </div>

      <div className="hero hero-1" style={{backgroundColor: 'var(--secondary-color)', padding: "1rem", paddingBottom:'2rem', margin: '2rem auto', marginBottom: '0'}}>
        <h2 className="section-heading">Case Study</h2>
        <div className="case-study-container">
          <div className="section-content" style={{boxShadow: 'none'}}>
            <h3 className="case-study-title">TaskFlow</h3>
            <p className="case-study-text">
              TaskFlow, a productivity app startup, used InstantFlows to validate their concept before development.
              <br/><br/>
              Their findings:
            </p>
            <div className="findings-list">
              {findings.map((finding, index) => (
                <p key={index} className="finding-item">
                  <span className="bullet-point">•</span>
                  <span dangerouslySetInnerHTML={{ __html: finding }}></span>
                </p>
              ))}
            </div>
            <p className="case-study-conclusion">
              These insights helped TaskFlow refine their MVP and secure their first round of funding.
            </p>
          </div>
        </div>
      </div>

      <div className="hero hero-2" style={{backgroundColor: 'var(--tertiary-color)', paddingTop: '4rem'}}>
        <div className="textbox" style={{width: "70%", background: "transparent", boxShadow: "none", color: "white"}}>
          <blockquote style={{fontSize: "1.2rem", fontStyle: "italic", lineHeight: '2.2rem'}}>
            "InstantFlows saved us from building the wrong product. In just 20 minutes, we identified a critical flaw in our assumption about what customers wanted. This would have cost us months of development time and thousands in wasted resources."
          </blockquote>
          <div style={{display: "flex", justifyContent: "right", alignItems: "center", marginTop: "2rem"}}>
            <div style={{textAlign: "right", marginLeft: "1rem"}}>
              <p style={{margin: 0, fontWeight: "600"}}>Sarah Johnson</p>
              <p style={{margin: 0, fontSize: "0.9rem"}}>Founder, NexTech Solutions</p>
            </div>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <h2 className="cta-title">Ready to Validate Your Concept?</h2>
        <p className="cta-description">Start getting insights in minutes, not weeks.</p>
        <div className="button-container" style={{maxHeight: "none"}}>
          <button onClick={handleTryTemplate} className="standout-button button">Try this template</button>
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="examples-section">
        <h2 className="examples-title" style={{marginTop: '0'}}>Example Researches</h2>
        <div className="examples-grid">
          <div 
            className="example-card" 
            onClick={() => handleViewExample('example1')}
          >
            <img 
              className="example-image" 
              src="/Example-1.png" 
              alt="Product Market Fit Analysis" 
            />
            <h3 className="example-title">Product Market Fit Analysis</h3>
            <p className="example-description">
              See how a tech startup validated their app concept by testing user intent and feature prioritization.
            </p>
            <button className="button example-button">View Example</button>
          </div>
          
          <div 
            className="example-card" 
            onClick={() => handleViewExample('example2')}
          >
            <img 
              className="example-image" 
              src="/Example-1.png" 
              alt="Pricing Strategy Test" 
            />
            <h3 className="example-title">Pricing Strategy Test</h3>
            <p className="example-description">
              Explore how a SaaS company determined their optimal pricing model and subscription tiers.
            </p>
            <button className="button example-button">View Example</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StartupValidationPage;